import { api } from './client';
import {
  EvaluationPeriod,
  EvaluationCriterion,
  EvaluationAssignment,
  EvaluationScore,
  EvaluationResult,
  EvaluationInterview,
  EvaluationAppeal,
  EvaluationNotification,
  EvaluationRadarData,
  CompanyValue,
  InterviewStats,
  NotificationCompliance,
  AppealDeadlineStatus,
  BiasAnalysis,
  GradeCalibration,
  DistributionSuggestionsResponse,
  MultiRadarData,
} from './types';

export const evaluationsApi = {
  getEvaluationPeriods: async (year?: number, status?: string): Promise<EvaluationPeriod[]> => {
    const params: any = {};
    if (year) params.year = year;
    if (status) params.status = status;
    const response = await api.get('/api/evaluations/periods', { params });
    return response.data;
  },

  createEvaluationPeriod: async (data: {
    name: string; period_type: string; year: number;
    half?: number; quarter?: number;
    start_date: string; end_date: string; description?: string;
  }): Promise<EvaluationPeriod> => {
    const response = await api.post('/api/evaluations/periods', data);
    return response.data;
  },

  getEvaluationPeriod: async (periodId: number): Promise<EvaluationPeriod> => {
    const response = await api.get(`/api/evaluations/periods/${periodId}`);
    return response.data;
  },

  updateEvaluationPeriod: async (periodId: number, data: { name?: string; start_date?: string; end_date?: string; description?: string }): Promise<any> => {
    const response = await api.put(`/api/evaluations/periods/${periodId}`, data);
    return response.data;
  },

  updateEvaluationPeriodStatus: async (periodId: number, status: string): Promise<any> => {
    const response = await api.put(`/api/evaluations/periods/${periodId}/status`, { status });
    return response.data;
  },

  getEvaluationCriteria: async (axis?: string, managerOnly?: boolean): Promise<EvaluationCriterion[]> => {
    const params: any = {};
    if (axis) params.axis = axis;
    if (managerOnly !== undefined) params.manager_only = managerOnly;
    const response = await api.get('/api/evaluations/criteria', { params });
    return response.data;
  },

  createEvaluationCriterion: async (data: { code: string; axis: string; name: string; weight: number; max_score?: number; description?: string; sort_order?: number; is_manager_only?: boolean }): Promise<any> => {
    const response = await api.post('/api/evaluations/criteria', data);
    return response.data;
  },

  updateEvaluationCriterion: async (criteriaId: number, data: any): Promise<any> => {
    const response = await api.put(`/api/evaluations/criteria/${criteriaId}`, data);
    return response.data;
  },

  deleteEvaluationCriterion: async (criteriaId: number): Promise<any> => {
    const response = await api.delete(`/api/evaluations/criteria/${criteriaId}`);
    return response.data;
  },

  getEvaluationAssignments: async (periodId: number, evaluateeId?: string): Promise<EvaluationAssignment[]> => {
    const params: any = { period_id: periodId };
    if (evaluateeId) params.evaluatee_id = evaluateeId;
    const response = await api.get('/api/evaluations/assignments', { params });
    return response.data;
  },

  autoAssignEvaluators: async (periodId: number, includeSecondary: boolean = false): Promise<any> => {
    const response = await api.post('/api/evaluations/assignments/auto', {
      period_id: periodId,
      include_secondary: includeSecondary,
    });
    return response.data;
  },

  reopenAssignment: async (assignmentId: number): Promise<any> => {
    const response = await api.post(`/api/evaluations/assignments/${assignmentId}/reopen`);
    return response.data;
  },

  submitEvaluationScores: async (assignmentId: number, scores: { criteria_id: number; score: number; comment?: string }[]): Promise<any> => {
    const response = await api.post(`/api/evaluations/scores/${assignmentId}/bulk`, { scores });
    return response.data;
  },

  getEvaluationScores: async (assignmentId: number): Promise<EvaluationScore[]> => {
    const response = await api.get(`/api/evaluations/scores/${assignmentId}`);
    return response.data;
  },

  saveScoresDraft: async (assignmentId: number, scores: { criteria_id: number; score: number; comment?: string }[]): Promise<any> => {
    const response = await api.post(`/api/evaluations/scores/${assignmentId}/draft`, { scores });
    return response.data;
  },

  finalizeScores: async (assignmentId: number): Promise<any> => {
    const response = await api.post(`/api/evaluations/scores/${assignmentId}/finalize`);
    return response.data;
  },

  getScoreProgress: async (assignmentId: number): Promise<any> => {
    const response = await api.get(`/api/evaluations/scores/${assignmentId}/progress`);
    return response.data;
  },

  getEvaluationResults: async (periodId: number, grade?: string): Promise<EvaluationResult[]> => {
    const params: any = { period_id: periodId };
    if (grade) params.grade = grade;
    const response = await api.get('/api/evaluations/results', { params });
    return response.data;
  },

  getEvaluationResult: async (resultId: number): Promise<EvaluationResult> => {
    const response = await api.get(`/api/evaluations/results/${resultId}`);
    return response.data;
  },

  getEmployeeEvaluationResults: async (employeeId: string): Promise<EvaluationResult[]> => {
    const response = await api.get(`/api/evaluations/results/employee/${employeeId}`);
    return response.data;
  },

  getPeriodProgress: async (periodId: number): Promise<any> => {
    const response = await api.get(`/api/evaluations/periods/${periodId}/progress`);
    return response.data;
  },

  getMyAssignments: async (periodId?: number): Promise<any> => {
    const params: any = {};
    if (periodId) params.period_id = periodId;
    const response = await api.get('/api/evaluations/my-assignments', { params });
    return response.data;
  },

  getEvaluationSummary: async (periodId: number): Promise<any> => {
    const response = await api.get('/api/evaluations/summary', { params: { period_id: periodId } });
    return response.data;
  },

  getWeightProfiles: async (): Promise<any> => {
    const response = await api.get('/api/evaluations/weight-profiles');
    return response.data;
  },

  // === 면담 (interviews) ===
  getEvaluationInterviews: async (resultId?: number): Promise<EvaluationInterview[]> => {
    const params: any = {};
    if (resultId) params.result_id = resultId;
    const response = await api.get('/api/evaluations/interviews', { params });
    return response.data;
  },

  createEvaluationInterview: async (data: {
    result_id: number; interviewer_id: string; interviewee_id: string;
    interview_date: string; interview_type?: string;
    content?: string; goals?: string; agreed_actions?: string;
  }): Promise<EvaluationInterview> => {
    const response = await api.post('/api/evaluations/interviews', data);
    return response.data;
  },

  scheduleInterview: async (data: {
    period_id: number; interviewer_id: string; interviewee_id: string;
    interview_date: string;
  }): Promise<{ message: string; id: number; result_id: number; status: string }> => {
    const response = await api.post('/api/evaluations/interviews/schedule', data);
    return response.data;
  },

  completeInterview: async (interviewId: number, data: {
    structured_content: Record<string, string>;
    goals?: string; agreed_actions?: string;
    private_notes?: string;
  }): Promise<{ message: string; id: number; status: string }> => {
    const response = await api.post(`/api/evaluations/interviews/${interviewId}/complete`, data);
    return response.data;
  },

  signInterview: async (interviewId: number): Promise<{ message: string; id: number; status: string }> => {
    const response = await api.post(`/api/evaluations/interviews/${interviewId}/sign`);
    return response.data;
  },

  getInterview: async (interviewId: number): Promise<EvaluationInterview> => {
    const response = await api.get(`/api/evaluations/interviews/${interviewId}`);
    return response.data;
  },

  getEmployeeInterviews: async (employeeId: string): Promise<{ employee_id: string; interviews: EvaluationInterview[]; total: number }> => {
    const response = await api.get(`/api/evaluations/interviews/employee/${encodeURIComponent(employeeId)}`);
    return response.data;
  },

  getInterviewStats: async (periodId: number): Promise<InterviewStats> => {
    const response = await api.get('/api/evaluations/interviews/stats', { params: { period_id: periodId } });
    return response.data;
  },

  getPendingInterviews: async (periodId?: number, interviewerId?: string): Promise<{ interviews: EvaluationInterview[]; total: number }> => {
    const params: any = {};
    if (periodId) params.period_id = periodId;
    if (interviewerId) params.interviewer_id = interviewerId;
    const response = await api.get('/api/evaluations/interviews/pending', { params });
    return response.data;
  },

  getInterviewEligible: async (periodId: number): Promise<{ employees: any[]; total: number }> => {
    const response = await api.get('/api/evaluations/interviews/eligible', { params: { period_id: periodId } });
    return response.data;
  },

  // === 이의제기 (appeals) ===
  getEvaluationAppeals: async (periodId?: number, status?: string): Promise<EvaluationAppeal[]> => {
    const params: any = {};
    if (periodId) params.period_id = periodId;
    if (status) params.status = status;
    const response = await api.get('/api/evaluations/appeals', { params });
    return response.data;
  },

  createEvaluationAppeal: async (data: { result_id: number; appeal_reason: string }): Promise<EvaluationAppeal> => {
    const response = await api.post('/api/evaluations/appeals', data);
    return response.data;
  },

  reviewEvaluationAppeal: async (appealId: number, data: { status: string; review_comment: string; revised_grade?: string }): Promise<any> => {
    const response = await api.put(`/api/evaluations/appeals/${appealId}/review`, data);
    return response.data;
  },

  getAppealDeadlines: async (periodId: number): Promise<AppealDeadlineStatus> => {
    const response = await api.get('/api/evaluations/appeals/deadlines', { params: { period_id: periodId } });
    return response.data;
  },

  getBiasAnalysis: async (periodId: number): Promise<BiasAnalysis> => {
    const response = await api.get('/api/evaluations/appeals/bias-analysis', { params: { period_id: periodId } });
    return response.data;
  },

  createAppealWithEvidence: async (data: { result_id: number; appeal_reason: string; evidence_text?: string }): Promise<any> => {
    const response = await api.post('/api/evaluations/appeals/with-evidence', data);
    return response.data;
  },

  assignAppealReviewer: async (appealId: number, reviewerId: string): Promise<any> => {
    const response = await api.put(`/api/evaluations/appeals/${appealId}/assign`, { reviewer_id: reviewerId });
    return response.data;
  },

  // === 통지 (notifications) ===
  getEvaluationNotifications: async (recipientId?: string): Promise<EvaluationNotification[]> => {
    const params: any = {};
    if (recipientId) params.recipient_id = recipientId;
    const response = await api.get('/api/evaluations/notifications', { params });
    return response.data;
  },

  acknowledgeEvaluationNotification: async (notificationId: number): Promise<any> => {
    const response = await api.put(`/api/evaluations/notifications/${notificationId}/acknowledge`);
    return response.data;
  },

  getNotificationCompliance: async (periodId: number): Promise<NotificationCompliance> => {
    const response = await api.get('/api/evaluations/notifications/compliance', { params: { period_id: periodId } });
    return response.data;
  },

  getNotificationSummary: async (periodId: number): Promise<any> => {
    const response = await api.get('/api/evaluations/notifications/summary', { params: { period_id: periodId } });
    return response.data;
  },

  sendBulkNotifications: async (periodId: number): Promise<any> => {
    const response = await api.post('/api/evaluations/notifications/bulk', { period_id: periodId });
    return response.data;
  },

  // === 등급 산정 + 강제 배분 + 보정 + 레이더 (BACK-143) ===
  calculatePeriodGrades: async (periodId: number): Promise<any> => {
    const response = await api.post('/api/evaluations/grades/calculate', { period_id: periodId });
    return response.data;
  },

  getDistributionSuggestions: async (periodId: number): Promise<DistributionSuggestionsResponse> => {
    const response = await api.get('/api/evaluations/distribution/suggestions', { params: { period_id: periodId } });
    return response.data;
  },

  applyCalibration: async (resultId: number, newGrade: string, reason: string): Promise<GradeCalibration> => {
    const response = await api.post(`/api/evaluations/calibrations/${resultId}`, { new_grade: newGrade, reason });
    return response.data;
  },

  getCalibrationHistory: async (periodId?: number, resultId?: number): Promise<GradeCalibration[]> => {
    const params: any = {};
    if (periodId) params.period_id = periodId;
    if (resultId) params.result_id = resultId;
    const response = await api.get('/api/evaluations/calibrations/history', { params });
    return response.data;
  },

  revertCalibration: async (calibrationId: number): Promise<any> => {
    const response = await api.post(`/api/evaluations/calibrations/${calibrationId}/revert`);
    return response.data;
  },

  // === 레이더 차트 ===
  getEvaluationRadar: async (employeeId: string, periodId?: number): Promise<EvaluationRadarData> => {
    const params: any = {};
    if (periodId) params.period_id = periodId;
    const response = await api.get(`/api/evaluations/radar/${employeeId}`, { params });
    return response.data;
  },

  getHrRadar: async (employeeId: string, periodId: number): Promise<MultiRadarData> => {
    const response = await api.get(`/api/evaluations/radar/hr/${encodeURIComponent(employeeId)}`, { params: { period_id: periodId } });
    return response.data;
  },

  getOrgRadar: async (orgUnitId: number, periodId: number): Promise<MultiRadarData> => {
    const response = await api.get(`/api/evaluations/radar/org/${orgUnitId}`, { params: { period_id: periodId } });
    return response.data;
  },

  getLeaderRadar: async (employeeId: string, periodId: number): Promise<MultiRadarData> => {
    const response = await api.get(`/api/evaluations/radar/leader/${encodeURIComponent(employeeId)}`, { params: { period_id: periodId } });
    return response.data;
  },

  // === 인재상 (Company Values) ===
  getCompanyValues: async (): Promise<{ values: CompanyValue[]; total: number }> => {
    const response = await api.get('/api/evaluations/company-values');
    return response.data;
  },

  createCompanyValue: async (data: { keyword: string; description?: string; sort_order?: number }): Promise<any> => {
    const response = await api.post('/api/evaluations/company-values', data);
    return response.data;
  },

  updateCompanyValue: async (id: number, data: { keyword?: string; description?: string; sort_order?: number }): Promise<any> => {
    const response = await api.put(`/api/evaluations/company-values/${id}`, data);
    return response.data;
  },

  deleteCompanyValue: async (id: number): Promise<any> => {
    const response = await api.delete(`/api/evaluations/company-values/${id}`);
    return response.data;
  },

  // === 그룹사별 평가 정책 (BACK-118) ===
  getEvalPolicies: async (companyId?: number): Promise<any> => {
    const response = await api.get('/api/eval-policies', { params: companyId ? { company_id: companyId } : {} });
    return response.data;
  },

  getEvalPolicySummary: async (): Promise<any> => {
    const response = await api.get('/api/eval-policies/summary');
    return response.data;
  },

  getEvalPolicy: async (policyId: number): Promise<any> => {
    const response = await api.get(`/api/eval-policies/${policyId}`);
    return response.data;
  },

  createEvalPolicy: async (data: any): Promise<any> => {
    const response = await api.post('/api/eval-policies', data);
    return response.data;
  },

  updateEvalPolicy: async (policyId: number, data: any): Promise<any> => {
    const response = await api.patch(`/api/eval-policies/${policyId}`, data);
    return response.data;
  },

  deleteEvalPolicy: async (policyId: number): Promise<any> => {
    const response = await api.delete(`/api/eval-policies/${policyId}`);
    return response.data;
  },

  getEvalCriteria: async (policyId: number): Promise<any> => {
    const response = await api.get(`/api/eval-policies/${policyId}/criteria`);
    return response.data;
  },

  addEvalCriteria: async (policyId: number, data: any): Promise<any> => {
    const response = await api.post(`/api/eval-policies/${policyId}/criteria`, data);
    return response.data;
  },

  updateEvalCriteria: async (templateId: number, data: any): Promise<any> => {
    const response = await api.patch(`/api/eval-policies/criteria/${templateId}`, data);
    return response.data;
  },

  deleteEvalCriteria: async (templateId: number): Promise<any> => {
    const response = await api.delete(`/api/eval-policies/criteria/${templateId}`);
    return response.data;
  },

  compareEvalPolicies: async (policyIds: number[]): Promise<any> => {
    const response = await api.post('/api/eval-policies/compare', policyIds);
    return response.data;
  },

  // === 그룹 평가 보고서/벤치마킹 (BACK-120) ===
  getGroupEvalSummary: async (periodId?: number): Promise<any> => {
    const response = await api.get('/api/group/evaluation/summary', { params: periodId ? { period_id: periodId } : {} });
    return response.data;
  },

  getGroupEvalBenchmark: async (periodId?: number): Promise<any> => {
    const response = await api.get('/api/group/evaluation/benchmark', { params: periodId ? { period_id: periodId } : {} });
    return response.data;
  },

  getGroupTurnoverRisk: async (): Promise<any> => {
    const response = await api.get('/api/group/evaluation/turnover-risk');
    return response.data;
  },

  // === 평가 보정 (BACK-121) ===
  getGradeDistribution: async (periodId: number, companyId?: number): Promise<any> => {
    const params: any = { period_id: periodId };
    if (companyId) params.company_id = companyId;
    const response = await api.get('/api/calibration/distribution', { params });
    return response.data;
  },

  createCalibrationSession: async (data: any): Promise<any> => {
    const response = await api.post('/api/calibration/sessions', data);
    return response.data;
  },

  getCalibrationSessions: async (periodId?: number): Promise<any> => {
    const params: any = {};
    if (periodId) params.period_id = periodId;
    const response = await api.get('/api/calibration/sessions', { params });
    return response.data;
  },

  getCalibrationSession: async (id: number): Promise<any> => {
    const response = await api.get(`/api/calibration/sessions/${id}`);
    return response.data;
  },

  saveCalibrationAdjustments: async (sessionId: number, adjustments: any[]): Promise<any> => {
    const response = await api.put(`/api/calibration/sessions/${sessionId}/adjustments`, { adjustments });
    return response.data;
  },

  applyCalibrationSession: async (sessionId: number): Promise<any> => {
    const response = await api.post(`/api/calibration/sessions/${sessionId}/apply`);
    return response.data;
  },

  getCalibrationComparison: async (periodId: number): Promise<any> => {
    const response = await api.get('/api/calibration/comparison', { params: { period_id: periodId } });
    return response.data;
  },
};
